// ── cohort-participants-collection: roster adapter for participants tab ─────

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingParticipantDto } from "../../../services/coaching-dtos";
import { orDash } from "../cohort-detail-helpers";
import {
  PARTICIPANT_STATUSES,
  participantStatusLabel,
  participantStatusTone
} from "../../participants/participants-helpers";
import { COHORT_TAB_PAGE_SIZE } from "./cohort-tab-collection-shared";

export type ParticipantRow = CoachingParticipantDto & Record<string, unknown>;

export function buildParticipantsFilterDefs(): CollectionFilterDef<ParticipantRow>[] {
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "active",
      options: PARTICIPANT_STATUSES.map((status) => ({
        value: status,
        label: participantStatusLabel(status)
      }))
    }
  ];
}

export function buildParticipantsCollectionColumns(): CollectionColumnDef<ParticipantRow>[] {
  return [
    {
      id: "name",
      header: "Participant",
      searchValue: (row) => `${row.name ?? ""} ${row.email ?? ""}`,
      priority: 1,
      cell: (row) => orDash(row.name)
    },
    {
      id: "email",
      header: "Email",
      priority: 2,
      cell: (row) => orDash(row.email)
    },
    {
      id: "status",
      header: "Status",
      priority: 2,
      cell: (row) => (
        <Badge variant={participantStatusTone(row.status)}>{participantStatusLabel(row.status)}</Badge>
      )
    }
  ];
}

export function useCohortParticipantsCollection(rows: CoachingParticipantDto[]): UseCollectionResult<ParticipantRow> {
  const filters = useMemo(() => buildParticipantsFilterDefs(), []);
  const columns = useMemo(() => buildParticipantsCollectionColumns(), []);

  return useCollection<ParticipantRow>({
    mode: "client",
    rows: rows as ParticipantRow[],
    columns,
    filters,
    pageSize: COHORT_TAB_PAGE_SIZE,
    urlSync: false,
    getRowKey: (row) => row.id
  });
}
